import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Affix, Input, Switch, Typography, Button,
  Form, notification,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';

import MapQueryTypes from '../../../services/query-types/map';
import { getPlatformMetadata, putObsDataset, postDatasetMetadata } from '../../../services/dataset.service';
import Partners from '../ConsoleDetail/Partners';
import { PLATFORM_TYPES } from './deployments/constants';
import ConfirmationModal from './ConfirmationModal';

const InformationTab = (props) => {
  const { obsDatasetSummary, organization } = props;

  const {
    data: platformMetadata,
  } = useQuery([MapQueryTypes.REST_PLATFORM_METADATA, obsDatasetSummary.obs_dataset_id],
    () => getPlatformMetadata(obsDatasetSummary.obs_dataset_id),
    {
      refetchOnWindowFocus: false,
      enabled: !!obsDatasetSummary?.obs_dataset_id,
    });

  const summary = platformMetadata?.find((m) => m.attribute_name === 'summary')?.attribute_value || '';
  const wmoPlatformCode = platformMetadata?.find((m) => m.attribute_name === 'wmo_platform_code')?.attribute_value || '';

  const [form] = Form.useForm();

  useEffect(() => {
    if (organization?.name) {
      form.setFieldsValue({
        organization_name: organization.name,
      });
    }
  }, [organization]);

  useEffect(() => {
    if (obsDatasetSummary?.platform_name) {
      form.setFieldsValue({
        platform_name: obsDatasetSummary.platform_name,
      });
    }

    if (obsDatasetSummary?.org_platform_id) {
      form.setFieldsValue({
        org_platform_id: obsDatasetSummary.org_platform_id,
      });
    }

    if (obsDatasetSummary?.ndbc_id) {
      form.setFieldsValue({
        ndbc_id: obsDatasetSummary.ndbc_id,
      });
    }

    if (obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type) {
      form.setFieldsValue({
        platform_type: PLATFORM_TYPES.find((p) => p.value === obsDatasetSummary.obs_dataset_platform_assignment.platform.platform_type)?.label,
      });
    }

    if (obsDatasetSummary) {
      form.setFieldsValue({
        ncei_archiving: obsDatasetSummary.ncei_archiving || false,
        ndbc_sharing: obsDatasetSummary.ndbc_sharing || false,
      });
    }
  }, [obsDatasetSummary]);

  useEffect(() => {
    if (summary) {
      form.setFieldsValue({
        summary,
      });
    }

    if (wmoPlatformCode) {
      form.setFieldsValue({
        wmo_platform_code: wmoPlatformCode,
      });
    }
  }, [platformMetadata]);

  const queryClient = useQueryClient();

  const obsDatasetMutation = useMutation(
    (payload) => putObsDataset(obsDatasetSummary?.obs_dataset_id, payload),
    {
      onSettled: () => {
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES_DATASET_DETAIL);
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES);
        // TODO: use the same cache key for summaries everywhere?
      },
      onSuccess: () => {
        notification.success({
          message: 'Success',
          description: 'Dataset saved successfully.',
        });
      },
      onError: (error) => {
        notification.error({
          message: 'Error',
          description: error.message || 'Failed to save dataset.',
        });
      },
    },
  );

  const obsDatasetMetadataMutation = useMutation(
    (payload) => postDatasetMetadata(obsDatasetSummary?.obs_dataset_id, payload),
    {
      onSettled: () => {
        queryClient.invalidateQueries([MapQueryTypes.REST_PLATFORM_METADATA, obsDatasetSummary.obs_dataset_id]);
      },
      onSuccess: () => {
        notification.success({
          message: 'Success',
          description: 'Dataset metadata saved successfully.',
        });
      },
      onError: (error) => {
        notification.error({
          message: 'Error',
          description: error.message || 'Failed to save dataset metadata.',
        });
      },
    },
  );

  const handleSubmit = () => {
    const fieldValues = form.getFieldsValue();

    const datasetPayload = {
      platform_name: fieldValues.platform_name,
      org_platform_id: fieldValues.org_platform_id,
      ncei_archiving: fieldValues.ncei_archiving,
      ndbc_sharing: fieldValues.ndbc_sharing,
      ndbc_id: fieldValues.ndbc_id,
    };

    obsDatasetMutation.mutate(datasetPayload);

    const metadataPayload = [{
      attribute_name: 'summary',
      attribute_value: fieldValues.summary,
    }, {
      attribute_name: 'wmo_platform_code',
      attribute_value: fieldValues.wmo_platform_code,
    }];

    obsDatasetMetadataMutation.mutate(metadataPayload);
  };

  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState(null);
  const [confirmationModalContent, setConfirmationModalContent] = useState(null);
  const [pendingFieldName, setPendingFieldName] = useState(null);

  const handleConfirmationModalOk = () => {
    setConfirmationModalIsOpen(false);
    form.setFieldsValue({
      [pendingFieldName]: false,
    });
  };

  const handleConfirmationModalCancel = () => {
    setConfirmationModalIsOpen(false);
    form.setFieldsValue({
      [pendingFieldName]: true,
    });
  };

  const onFieldsChange = (changedFields) => {
    const name = changedFields[0]?.name[0];
    const value = changedFields[0]?.value;

    setPendingFieldName(name);

    if (!value) {
      if (name === 'ndbc_sharing') {
        setConfirmationModalTitle('Disable NDBC Sharing');
        setConfirmationModalContent('Are you sure you want to disable NDBC sharing? This will stop sending data to NDBC.');
        setConfirmationModalIsOpen(true);
      } else if (name === 'ncei_archiving') {
        setConfirmationModalTitle('Disable NCEI Archiving');
        setConfirmationModalContent('Are you sure you want to disable NCEI archiving? This will stop archiving data to NCEI.');
        setConfirmationModalIsOpen(true);
      }
    } else if (name === 'ndbc_sharing' && !form.getFieldValue('ndbc_id')) {
      setConfirmationModalTitle('NDBC ID Required');
      setConfirmationModalContent('Please provide an NDBC ID before enabling NDBC sharing.');
      setConfirmationModalIsOpen(true);
    } else if (name === 'ncei_archiving' && !form.getFieldValue('wmo_platform_code')) {
      setConfirmationModalTitle('WMO Code Required');
      setConfirmationModalContent('Please provide a WMO Platform Code before enabling NCEI archiving.');
      setConfirmationModalIsOpen(true);
    }
  };

  return (
    <div>
      <ConfirmationModal
        visible={confirmationModalIsOpen}
        title={confirmationModalTitle}
        content={confirmationModalContent}
        handleOk={handleConfirmationModalOk}
        handleCancel={handleConfirmationModalCancel}
      />
      <Form
        form={form}
        className="dataset-information-form"
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={onFieldsChange}
        initialValues={{
          platform_name: obsDatasetSummary?.platform_name || '',
          org_platform_id: obsDatasetSummary?.org_platform_id || '',
          organization_name: organization?.name || '',
          summary: summary || '',
        }}
      >
        <div
          style={{
            display: 'grid',
            gap: 20,
            gridTemplateColumns: '1fr 2fr',
          }}
        >
          <div>
            <Typography.Title
              level={4}
              style={{
                color: '#DAE4EA',
              }}
            >
              Basic Information
            </Typography.Title>
            <Form.Item
              label="Platform Name"
              name="platform_name"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Organization Platform ID"
              name="org_platform_id"
            >
              <Input
                readOnly
                suffix={<LockOutlined />}
              />
            </Form.Item>
            <Form.Item
              label="Platform Type"
              name="platform_type"
            >
              <Input
                readOnly
                suffix={<LockOutlined />}
              />
            </Form.Item>
            <Form.Item
              label="Organization"
              name="organization_name"
            >
              <Input
                readOnly
                suffix={<LockOutlined />}
                value={organization?.name}
              />
            </Form.Item>
            <Form.Item
              label="NDBC ID"
              name="ndbc_id"
            >
              <Input
                readOnly={Boolean(obsDatasetSummary.ndbc_id)}
                suffix={obsDatasetSummary.ndbc_id ? <LockOutlined /> : ''}
              />
            </Form.Item>
            <Form.Item
              label="Send data to NDBC"
              name="ndbc_sharing"
              className="switch-input"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="WMO Platform Code"
              name="wmo_platform_code"
            >
              <Input
                readOnly={Boolean(wmoPlatformCode)}
                suffix={wmoPlatformCode ? <LockOutlined /> : ''}
              />
            </Form.Item>
            <Form.Item
              label="Archive data to NCEI"
              name="ncei_archiving"
              className="switch-input"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Summary"
              name="summary"
            >
              <Input.TextArea
                rows={4}
                autoSize
              />
            </Form.Item>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
            >
              <div>
                <label
                  className="dataset-information-label"
                >
                  Latitude
                </label>
                <Input
                  className="dataset-information-input"
                  readOnly
                  suffix={<LockOutlined color="#748D9C" />}
                  value={obsDatasetSummary?.deployment_site?.latitude}
                />
              </div>
              <div>
                <label
                  className="dataset-information-label"
                >
                  Longitude
                </label>
                <Input
                  className="dataset-information-input"
                  readOnly
                  suffix={<LockOutlined style={{ color: '748D9C' }} color="#748D9C" />}
                  value={obsDatasetSummary?.deployment_site?.longitude}
                />
              </div>
            </div>
          </div>
          <div>
            <Typography.Title
              level={4}
              style={{
                color: '#DAE4EA',
              }}
            >
              Partners
            </Typography.Title>
            <div className="information-tab-partners">
              <Partners partners={obsDatasetSummary?.obs_dataset_partners} />
            </div>
          </div>
        </div>
        <Affix
          offsetBottom={0}
          offsetLeft={0}
          offsetRight={0}
          className="information-form-actions"
        >
          <Button
            type="link"
            style={{
              color: '#DDE3E7',
              border: 'none',
              background: 'transparent',
            }}
          >
            Cancel
          </Button>
          <Button
            loading={obsDatasetMutation.isLoading || obsDatasetMetadataMutation.isLoading}
            htmlType="submit"
            style={{
              color: 'white',
              background: '#AA3E19',
              border: 'none',
              borderRadius: 20,
              height: 'auto',
              padding: '8px 12px',
              marginTop: 10,
              marginBottom: 10,
              marginRight: 10,
            }}
          >
            Save Changes
          </Button>
        </Affix>
      </Form>
    </div>
  );
};

InformationTab.propTypes = {
  obsDatasetSummary: PropTypes.object,
  organization: PropTypes.object,
};

InformationTab.defaultProps = {
  obsDatasetSummary: {},
  organization: {},
};

export default InformationTab;
